import Login from 'assets/images/nav/login.svg';
import Logout from 'assets/images/nav/logout.svg';
import User from 'assets/images/nav/user.svg';

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Popup } from 'semantic-ui-react';
import styled from 'styled-components';
import { handleOrgChange } from '../../../routing/utils/LoginUtils';
import { AppDispatch } from '../../../store/store';
import ImpersonationPickerModal from 'areas/settings/account-management/components/ImpersonationPickerModal';
import useFeatures from 'common/hooks/useFeatures';
import { isCurrentMemberOrgAdmin, isCurrentUserStaffOrSuperAdmin } from '../../selectors/auth';
import {
  getCurrentAccount,
  getCurrentOrganization,
  getOrganizations,
  isCurrentUserSuperUser,
  isUserAuthenticated,
} from '../../selectors/auth';
import {
  getImpersonationMode,
  getImpersonationRealUser,
  getImpersonationSessionId,
  ImpersonationMode,
} from '../../slices/impersonationSlice';
import { endImpersonation } from '../../utils/impersonationFlow';
import { Title } from '../../styles/page';
import { useSidebar } from '../../contexts/SidebarContext';
import { useToasts } from 'common/toast';
import CopilotIcon from 'common/components/icons/Copilot';

// Types
interface Organization {
  id: number;
  name: string;
}

const isLocalEnv = process.env.NODE_ENV === 'development';
// Vite always builds with NODE_ENV=production, so the deployment target is
// signalled separately via REACT_APP_ENVIRONMENT (set per Amplify branch).
const isStagingEnv = process.env.REACT_APP_ENVIRONMENT === 'staging';

const TopBar: React.FC<{
  setHelp?: () => void;
  topBarContent?: React.ReactNode;
  mode?: string;
  copilotOpen?: boolean;
  onCopilotToggle?: () => void;
}> = ({ setHelp, topBarContent, mode = 'default', copilotOpen = false, onCopilotToggle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isOpen } = useSidebar();
  const { addToast } = useToasts();

  const { isFeatureEnabled, initialized } = useFeatures();

  const isAuthenticated = useSelector(isUserAuthenticated);
  const isSuperUser = useSelector(isCurrentUserSuperUser);

  const impersonationMode = useSelector(getImpersonationMode);
  // Only show the impersonation banner/controls in ACTIVE — during
  // STARTING/ENDING the realUser/sessionId in Redux may be stale or in flight,
  // and the affordances would either show the wrong identity or risk a
  // double-DELETE on the session.
  const impersonating = impersonationMode === ImpersonationMode.ACTIVE;

  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);

  const realUser = useSelector(getImpersonationRealUser);
  const sessionId = useSelector(getImpersonationSessionId);
  const currentAccount = useSelector(getCurrentAccount) as
    | { firstName?: string | null; lastName?: string | null; email?: string | null }
    | undefined;
  const organizationList = (useSelector(getOrganizations) as Organization[]) || [];
  const sortedOrganizations = [...organizationList].sort((a, b) => a.name.localeCompare(b.name));
  const currentOrganization = useSelector(getCurrentOrganization) as Organization;

  const realUserLabel = realUser
    ? [realUser.firstName, realUser.lastName].filter(Boolean).join(' ').trim() || realUser.email
    : '';
  const impersonatedUserLabel = currentAccount
    ? [currentAccount.firstName, currentAccount.lastName].filter(Boolean).join(' ').trim()
      || currentAccount.email
      || ''
    : '';

  const isCopilotEnabled = initialized && isFeatureEnabled('Copilot Enhancements');
  const isCopilotVisible = isCopilotEnabled && (isOrgAdmin || isStaffOrSuperAdmin);

  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);

  const handleEndImpersonation = async () => {
    if (sessionId == null) return;
    try {
      await endImpersonation(dispatch, navigate, sessionId);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to end impersonation', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const profileTrigger = (
    <ProfileBadge $impersonating={impersonating}>
      <Image src={User} />
    </ProfileBadge>
  );

  const hasExtraMenuItems = (isStaffOrSuperAdmin && !impersonating) || impersonating;

  const profileMenu = isAuthenticated && (
    !hasExtraMenuItems ? (
      <Link to="/settings/user" aria-label="Profile Settings" title="Profile Settings">
        {profileTrigger}
      </Link>
    ) : (
    <Dropdown
      trigger={profileTrigger}
      icon={null}
      pointing="top right"
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <Dropdown.Menu
        style={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '1px solid #ddd',
          borderRadius: '4px',
          zIndex: 999,
        }}
      >
        {impersonating && (
          <>
            <Dropdown.Header as="div">
              <div>Impersonating {impersonatedUserLabel}</div>
            </Dropdown.Header>
            <Dropdown.Item
              text={`Switch back to ${realUserLabel}`}
              onClick={handleEndImpersonation}
            />
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item
          text="Profile Settings"
          onClick={() => navigate('/settings/user')}
        />
        {isStaffOrSuperAdmin && !impersonating && (
          <Dropdown.Item
            text="Impersonate User"
            onClick={() => setIsImpersonationModalOpen(true)}
          />
        )}
      </Dropdown.Menu>
    </Dropdown>
    )
  );

  const changeOrganization = async (org: Organization) => {
    if (org.id === currentOrganization.id) {
      return;
    }

    try {
      await handleOrgChange(dispatch, org);
      navigate('/app/dashboard');
    } catch (error) {
      addToast('Failed to switch organization. Please try again.', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  return mode === 'default' ? (
    <Container $isOpen={isOpen}>
      {/* LEFT side */}
      <LeftSection>
        {sortedOrganizations.length > 1 ? (
          <Dropdown
            text={currentOrganization?.name || ''}
            style={{
              boxShadow: 'none',
              fontFamily: "'Poppins', sans-serif",
              fontSize: '20px',
              fontWeight: 550,
              color: '#555',
              padding: 0,
            }}
          >
            <Dropdown.Menu
              style={{
                marginTop: '5px',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #ddd',
                borderRadius: '4px',
                zIndex: 999,
              }}
            >
              {sortedOrganizations.map(org => (
                <Dropdown.Item
                  key={org.id}
                  text={org.name}
                  onClick={() => changeOrganization(org)}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '14px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <Title>{currentOrganization?.name}</Title>
        )}
      </LeftSection>

      {/* RIGHT side */}
      <RightSection>
        <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
          {topBarContent}
        </div>

        {isCopilotVisible && onCopilotToggle && (
          <CopilotTriggerButton
            onClick={onCopilotToggle}
            title={copilotOpen ? 'Close Copilot' : 'Open Copilot'}
            aria-label={copilotOpen ? 'Close Copilot' : 'Open Copilot'}
            $active={copilotOpen}
          >
            <CopilotIcon />
            <ButtonLabel>Copilot</ButtonLabel>
          </CopilotTriggerButton>
        )}

        {profileMenu}
        <ImpersonationPickerModal
          open={isImpersonationModalOpen}
          onClose={() => setIsImpersonationModalOpen(false)}
        />

        {isAuthenticated ? (
          <Link to="/account/logout" data-testid="logout">
            <Popup content="Log Out" trigger={<Image src={Logout} />} />
          </Link>
        ) : (
          <Link to="/account/login">
            <Popup content="Log In" trigger={<Image src={Login} />} />
          </Link>
        )}
      </RightSection>
    </Container>
  ) : (
    <SettingsContainer $isOpen={isOpen}>
      {/* LEFT side */}
      <LeftSection>
        <Title color="white">nuLogic Admin</Title>
      </LeftSection>

      {/* RIGHT side */}
      <RightSection>
        <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
          {topBarContent}
        </div>

        {isAuthenticated && (
          <Link to="/settings/user">
            <Popup content="Profile Settings" trigger={<Image src={User} color="" />} />
          </Link>
        )}

        {isAuthenticated ? (
          <Link to="/account/logout" data-testid="logout">
            <Popup content="Log Out" trigger={<Image src={Logout} />} />
          </Link>
        ) : (
          <Link to="/account/login">
            <Popup content="Log In" trigger={<Image src={Login} />} />
          </Link>
        )}
      </RightSection>
    </SettingsContainer>
  );
};

export default TopBar;

// Styled Components
const Container = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: ${props => (props.$isOpen ? '250px' : '50px')};
  right: 0;
  height: 50px;
  z-index: 1000;
  background-color: ${isLocalEnv ? 'rgba(125, 192, 140, 0.15)' : isStagingEnv ? 'rgba(253, 224, 71, 0.15)' : 'rgba(165, 207, 238, 0.15)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  padding-left: 20px;
  font-size: 20px;
  border-bottom: 1px solid ${isLocalEnv ? 'rgba(85, 135, 80, 0.3)' : isStagingEnv ? 'rgba(245, 158, 11, 0.3)' : 'rgba(165, 207, 238, 0.3)'};
  transition: left 0.3s ease-in-out;
  min-width: 700px;
`;

const SettingsContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: ${props => (props.$isOpen ? '250px' : '50px')};
  right: 0;
  height: 50px;
  z-index: 1000;
  background-color: ${isLocalEnv ? 'rgba(35, 95, 45, 0.6)' : isStagingEnv ? 'rgba(180, 83, 9, 0.6)' : 'rgba(5, 38, 65, 0.6)'};
  color: white;

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  padding-left: 20px;
  font-size: 20px;
  border-bottom: 1px solid ${isLocalEnv ? 'rgba(25, 75, 40, 0.8)' : 'rgba(5, 38, 65, 0.8)'};
  transition: left 0.3s ease-in-out;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const Image = styled.img`
  height: 19px;
  width: 19px;
  margin-right: 20px;
`;

const ProfileBadge = styled.div<{ $impersonating: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 4px;
  margin-right: 12px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${props => (props.$impersonating ? 'rgba(180, 83, 9, 0.12)' : 'transparent')};
  box-shadow: ${props => (props.$impersonating ? '0 0 0 2px #b45309' : 'none')};
  transition: background-color 0.15s ease, box-shadow 0.15s ease;

  & > img {
    margin-right: 0;
  }

  &::after {
    content: '';
    display: ${props => (props.$impersonating ? 'block' : 'none')};
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 8px;
    background-color: #b45309;
    border: 2px solid #fff;
    border-radius: 50%;
  }
`;

const CopilotTriggerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0 8px;
  min-width: 0;
  height: 34px;
  border-radius: 6px;
  border: 1px solid
    ${props => (props.$active ? '#94a3b8' : '#cbd5e1')};
  background: ${props => (props.$active ? '#ffffff' : '#f8fafc')};
  color: ${props => (props.$active ? '#111827' : '#4b5563')};
  cursor: pointer;
  margin-right: 14px;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${props => (props.$active ? '#eef2ff' : 'rgba(148, 163, 184, 0.12)')};
    border-color: ${props => (props.$active ? '#94a3b8' : '#94a3b8')};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  svg,
  img {
    width: 16px;
    height: 16px;
  }
`;

const ButtonLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;
